import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate, Link } from "react-router-dom";
import { Menu, X, ChevronLeft, ChevronRight, MapPin, Clock, Mail, Phone, Instagram } from "lucide-react";
import axios from "axios";
import { Button } from "../components/ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Animation variants
const fadeUp = {
  initial: { opacity: 0, y: 40 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.8, ease: "easeOut" }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const staggerItem = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0 }
};

// Navigation Component
const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { href: "#yoga-arten", label: "Yoga" },
    { href: "#wochenplan", label: "Wochenplan" },
    { href: "#preise", label: "Beiträge" },
    { href: "#events", label: "Events" },
    { href: "#ueber-mich", label: "Über Mich" },
    { href: "#kontakt", label: "Kontakt" },
  ];

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? "bg-yoga-cream/95 backdrop-blur-sm shadow-sm" : "bg-transparent"}`}>
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          <a href="#" className="font-serif text-3xl md:text-4xl text-yoga-brown" data-testid="logo">
            DanyKizayYoga
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="nav-link text-yoga-brown font-sans text-sm tracking-wide hover:opacity-70 transition-opacity"
                data-testid={`nav-${link.href.slice(1)}`}
              >
                {link.label}
              </a>
            ))}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-yoga-brown"
            onClick={() => setIsOpen(!isOpen)}
            data-testid="mobile-menu-toggle"
          >
            {isOpen ? <X size={24} strokeWidth={1.5} /> : <Menu size={24} strokeWidth={1.5} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="mobile-menu md:hidden absolute top-20 left-0 right-0 py-6 px-6 shadow-lg"
          >
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                className="block py-3 text-yoga-brown font-sans text-lg border-b border-yoga-sand/50"
                onClick={() => setIsOpen(false)}
              >
                {link.label}
              </a>
            ))}
          </motion.div>
        )}
      </div>
    </nav>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section className="relative h-screen" data-testid="hero-section">
      <div
        className="absolute inset-0 bg-cover md:bg-center bg-[position:68%_0%]"
        style={{
          backgroundImage: `url('https://customer-assets.emergentagent.com/job_flow-wellness-3/artifacts/xo17ucms_SUB_0834_edited.jpg')`,
        }}
      />
      <div className="relative h-full flex flex-col items-center justify-end pb-4 md:pb-6 text-center px-6">
        <motion.a
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.5 }}
          href="#wochenplan"
          className="btn-primary bg-yoga-brown/70 backdrop-blur-sm text-yoga-cream px-8 py-4 rounded-full font-sans text-sm tracking-wide hover:bg-yoga-brown/80 mb-6"
          data-testid="hero-cta"
        >
          Zum Kursplan
        </motion.a>
        <motion.p
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.7 }}
          className="font-sans text-lg md:text-2xl text-yoga-brown/80 tracking-wide"
          data-testid="hero-subtitle"
        >
          Vinyasa Yoga & Inside Flow Yoga in Frechen
        </motion.p>
      </div>
    </section>
  );
};

// Yoga Arten Section
const YogaArtenSection = () => {
  return (
    <section id="yoga-arten" className="py-16 md:py-32 bg-yoga-cream" data-testid="yoga-arten-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div {...fadeUp} className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-20">
          {/* Vinyasa Yoga */}
          <div className="text-center">
            <h2 className="font-serif text-2xl md:text-4xl text-yoga-brown mb-6 md:mb-8">
              Vinyasa Yoga
            </h2>
            <div className="space-y-4 md:space-y-6 text-yoga-brown/80 font-sans leading-relaxed text-sm md:text-base">
              <p>
                Vinyasa Yoga ist ein dynamischer und fließender Yogastil – kraftvoll, aber zugleich geschmeidig.
              </p>
              <p>
                In meinen Stunden lege ich viel Wert auf eine detailverliebte Anleitung, mit vielen hilfreichen Hinweisen zur korrekten Ausrichtung. Dabei steht immer die Freude an der Bewegung im Vordergrund.
              </p>
              <p>
                Der Atem führt dich durch die Praxis und hilft dir, deine Zentrierung zu finden.
              </p>
              <p>
                Wir üben uns in Kräftigung, aber auch in Balance und Flexibilität, um Körper und Geist in Einklang zu bringen.
              </p>
            </div>
          </div>

          {/* Inside Flow Yoga */}
          <div className="text-center">
            <div className="relative inline-block mb-6 md:mb-8">
              <h2 className="font-serif text-2xl md:text-4xl text-yoga-brown">
                Inside Flow Yoga
              </h2>
              <img 
                src="https://customer-assets.emergentagent.com/job_flow-wellness-3/artifacts/gtuzrete_4c85e25e93f834f80ca65e4cfd0925da_square.png" 
                alt="Inside Flow Yoga Logo" 
                className="absolute -right-8 md:-right-14 top-1/2 -translate-y-1/2 w-6 h-6 md:w-12 md:h-12 object-contain"
                style={{ filter: 'invert(1) brightness(0.4) sepia(1) saturate(0.5)' }}
              />
            </div>
            <div className="space-y-4 md:space-y-6 text-yoga-brown/80 font-sans leading-relaxed text-sm md:text-base">
              <p>
                Inside Flow ist eine Weiterentwicklung des Vinyasa Yogas – ein besonderer Yogastil, der dein Herz berührt.
              </p>
              <p>
                Wir praktizieren Yoga on Beat zu moderner Popmusik. Dabei erlernst du eine choreografierte, sich aufbauende Abfolge von Yoga-Asanas, synchronisiert mit deinem Atem und der Musik.
              </p>
              <p>
                Am Ende der Stunde ist ein kompletter Song durchchoreografiert – jeder Song trägt seine ganz eigene Bedeutung und Message, die du durch deine Bewegungen auf die Matte bringst.
              </p>
              <p>
                Eine Stunde voller Spaß, Freude und Emotionen – für alle, die Musik lieben und sich gerne im Flow bewegen.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Weekly Schedule Section
const WochenplanSection = () => {
  const [schedule, setSchedule] = useState([]);
  const [currentWeekStart, setCurrentWeekStart] = useState(() => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    const monday = new Date(today);
    monday.setDate(today.getDate() + diff);
    monday.setHours(0, 0, 0, 0);
    return monday;
  });

  const days = ["Mo", "Di", "Mi", "Do", "Fr", "Sa", "So"];
  const fullDays = ["Montag", "Dienstag", "Mittwoch", "Donnerstag", "Freitag", "Samstag", "Sonntag"];

  useEffect(() => {
    fetchSchedule();
  }, []);

  const fetchSchedule = async () => {
    try {
      const response = await axios.get(`${API}/schedule`);
      setSchedule(response.data);
    } catch (error) {
      console.error("Error fetching schedule:", error);
    }
  };

  const getDateForDay = (dayIndex) => {
    const date = new Date(currentWeekStart);
    date.setDate(currentWeekStart.getDate() + dayIndex);
    return date;
  };

  const formatDate = (date) => {
    return date.getDate();
  };

  const getMonthYear = () => {
    const months = ["Januar", "Februar", "März", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Dezember"];
    const midWeek = new Date(currentWeekStart);
    midWeek.setDate(currentWeekStart.getDate() + 3);
    return `${months[midWeek.getMonth()]} ${midWeek.getFullYear()}`;
  };

  const isToday = (date) => {
    const today = new Date();
    return date.getDate() === today.getDate() && date.getMonth() === today.getMonth() && date.getFullYear() === today.getFullYear();
  };

  const isFriday = (dayIndex) => dayIndex === 4;

  const prevWeek = () => {
    const newStart = new Date(currentWeekStart);
    newStart.setDate(currentWeekStart.getDate() - 7);
    setCurrentWeekStart(newStart);
  };

  const nextWeek = () => {
    const newStart = new Date(currentWeekStart);
    newStart.setDate(currentWeekStart.getDate() + 7);
    setCurrentWeekStart(newStart);
  };

  const getEntriesForDay = (day) => {
    return schedule.filter(entry => entry.day === day).sort((a, b) => a.time.localeCompare(b.time));
  };

  return (
    <section id="wochenplan" className="py-16 md:py-32 bg-yoga-sand/30" data-testid="wochenplan-section">
      <div className="max-w-7xl mx-auto px-4 md:px-12">
        <motion.h2 {...fadeUp} className="font-serif text-3xl md:text-5xl text-yoga-brown text-center mb-8 md:mb-12">
          Wochenplan
        </motion.h2>

        {/* Week Navigation */}
        <motion.div {...fadeUp} className="flex items-center justify-center gap-4 md:gap-6 mb-6 md:mb-8">
          <button onClick={prevWeek} className="p-2 hover:bg-yoga-sand/50 rounded-full transition-colors" data-testid="prev-week">
            <ChevronLeft size={20} strokeWidth={1.5} className="text-yoga-brown" />
          </button>
          <span className="font-serif text-lg md:text-xl text-yoga-brown">{getMonthYear()}</span>
          <button onClick={nextWeek} className="p-2 hover:bg-yoga-sand/50 rounded-full transition-colors" data-testid="next-week">
            <ChevronRight size={20} strokeWidth={1.5} className="text-yoga-brown" />
          </button>
        </motion.div>

        {/* Desktop Schedule Grid */}
        <motion.div {...fadeUp} className="hidden md:block bg-yoga-cream rounded-xl overflow-hidden shadow-sm">
          {/* Days Header */}
          <div className="grid grid-cols-7 border-b border-yoga-sand">
            {days.map((day, index) => {
              const date = getDateForDay(index);
              const today = isToday(date);
              const friday = isFriday(index);
              return (
                <div key={day} className="p-4 text-center border-r border-yoga-sand last:border-r-0">
                  <div className={`font-sans text-sm ${friday ? "text-red-400" : "text-yoga-brown/60"}`}>{day}</div>
                  <div className={`font-sans text-lg ${today ? "text-yoga-sage font-bold" : friday ? "text-red-400" : "text-yoga-brown"}`}>
                    {formatDate(date)}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Schedule Entries */}
          <div className="grid grid-cols-7 min-h-[300px]">
            {days.map((day) => {
              const entries = getEntriesForDay(day);
              return (
                <div key={day} className="border-r border-yoga-sand last:border-r-0 p-3 space-y-3">
                  {entries.map((entry) => (
                    <div key={entry.id} className="text-center py-3 border-b border-yoga-sand/50 last:border-b-0">
                      <div className="font-sans text-sm text-yoga-brown/60">{entry.time}</div>
                      <div className="font-sans text-sm font-medium text-yoga-brown">{entry.title}</div>
                      {entry.location && (
                        <div className="font-sans text-xs text-yoga-brown/50">{entry.location}</div>
                      )}
                      <div className="font-sans text-xs text-yoga-brown/40 mt-1">{entry.duration}</div>
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        </motion.div>

        {/* Mobile Schedule */}
        <motion.div {...fadeUp} className="md:hidden space-y-4">
          {days.map((day, index) => {
            const entries = getEntriesForDay(day);
            const date = getDateForDay(index);
            if (entries.length === 0) return null;
            return (
              <div key={day} className="bg-yoga-cream rounded-xl p-4 shadow-sm">
                <div className="flex items-center justify-between mb-3 pb-3 border-b border-yoga-sand">
                  <span className="font-serif text-lg text-yoga-brown">{fullDays[index]}</span>
                  <span className={`font-sans text-sm ${isToday(date) ? "text-yoga-sage font-bold" : "text-yoga-brown/60"}`}>
                    {formatDate(date)}.
                  </span>
                </div>
                <div className="space-y-3">
                  {entries.map((entry) => (
                    <div key={entry.id} className="flex justify-between items-start">
                      <div>
                        <div className="font-sans text-sm font-medium text-yoga-brown">{entry.title}</div>
                        {entry.location && (
                          <div className="font-sans text-xs text-yoga-brown/50">{entry.location}</div>
                        )}
                      </div>
                      <div className="text-right">
                        <div className="font-sans text-sm text-yoga-brown/60">{entry.time}</div>
                        <div className="font-sans text-xs text-yoga-brown/40">{entry.duration}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

// Gallery Image Above Wochenplan
const GalleryTopSection = () => {
  return (
    <section className="relative bg-yoga-cream" data-testid="gallery-top-section">
      {/* Mobile: Portrait image */}
      <img 
        src="https://customer-assets.emergentagent.com/job_74b33882-e61a-42ed-be67-f1e060c4281a/artifacts/k47zwis6_WhatsApp%20Image%202026-03-17%20at%2010.48.08.jpeg"
        alt="Daniela Yoga Pose"
        className="w-full h-auto md:hidden"
      />
      {/* Desktop: Street pose - full image visible */}
      <div 
        className="hidden md:block h-[56.25vw] max-h-[80vh] bg-contain bg-center bg-no-repeat"
        style={{
          backgroundImage: `url('https://customer-assets.emergentagent.com/job_74b33882-e61a-42ed-be67-f1e060c4281a/artifacts/jo7zlle9_WhatsApp%20Image%202026-03-17%20at%2010.51.40.jpeg')`,
        }}
      />
    </section>
  );
};

// Gallery Image Below Wochenplan
const GalleryBottomSection = () => {
  return (
    <section className="relative bg-yoga-cream" data-testid="gallery-bottom-section">
      {/* Mobile: Portrait image */}
      <img 
        src="https://customer-assets.emergentagent.com/job_74b33882-e61a-42ed-be67-f1e060c4281a/artifacts/7m80wm91_WhatsApp%20Image%202026-03-17%20at%2010.47.51.jpeg"
        alt="Daniela Yoga Sunset"
        className="w-full h-auto md:hidden"
      />
      {/* Desktop: Handstand - full image visible */}
      <div 
        className="hidden md:block h-[56.25vw] max-h-[80vh] bg-contain bg-center bg-no-repeat"
        style={{
          backgroundImage: `url('https://customer-assets.emergentagent.com/job_74b33882-e61a-42ed-be67-f1e060c4281a/artifacts/8t4h093i_WhatsApp%20Image%202026-03-17%20at%2010.52.23.jpeg')`,
        }}
      />
    </section>
  );
};

// Prices Section
const PreiseSection = () => {
  const [prices, setPrices] = useState([]);

  useEffect(() => {
    fetchPrices();
  }, []);

  const fetchPrices = async () => {
    try {
      const response = await axios.get(`${API}/prices`);
      setPrices(response.data);
    } catch (error) {
      console.error("Error fetching prices:", error);
    }
  };

  const einzelpreise = prices.filter(p => !p.is_membership);
  const mitgliedschaften = prices.filter(p => p.is_membership);

  return (
    <section id="preise" className="py-16 md:py-32 bg-yoga-cream" data-testid="preise-section">
      <div className="max-w-7xl mx-auto px-4 md:px-12">
        <motion.h2 {...fadeUp} className="font-serif text-3xl md:text-5xl text-yoga-brown text-center mb-4 md:mb-6">
          Beiträge
        </motion.h2>
        <motion.p {...fadeUp} className="font-sans text-sm md:text-lg text-yoga-brown/70 text-center mb-8 md:mb-12 max-w-2xl mx-auto px-4">
          Flexible Optionen für jeden – ob Schnupperstunde oder regelmäßige Praxis
        </motion.p>

        {/* Einzelpreise */}
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-12 md:mb-16"
        >
          {einzelpreise.map((price) => (
            <motion.div
              key={price.id}
              variants={staggerItem}
              className="price-card bg-white/50 backdrop-blur-sm border border-yoga-sand/50 rounded-xl p-4 md:p-6 text-center"
            >
              <h3 className="font-serif text-lg md:text-xl text-yoga-brown mb-2">{price.title}</h3>
              <div className="font-serif text-2xl md:text-3xl text-yoga-brown mb-2 md:mb-3">{price.price}</div>
              {price.description && (
                <p className="font-sans text-xs md:text-sm text-yoga-brown/60 mb-1 md:mb-2">{price.description}</p>
              )}
              {price.validity && (
                <p className="font-sans text-xs text-yoga-sage">{price.validity}</p>
              )}
            </motion.div>
          ))}
        </motion.div>

        {/* Mitgliedschaften */}
        {mitgliedschaften.length > 0 && (
          <>
            <motion.h3 {...fadeUp} className="font-serif text-xl md:text-2xl text-yoga-brown text-center mb-3 md:mb-4">
              Monatsmitgliedschaften
            </motion.h3>
            <motion.p {...fadeUp} className="font-sans text-xs md:text-base text-yoga-brown/70 text-center mb-6 md:mb-8 max-w-xl mx-auto px-4">
              Die Mitgliedschaften lohnen sich besonders für alle, die regelmäßig zu meinen Stunden kommen.
            </motion.p>
            <motion.div
              variants={staggerContainer}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true }}
              className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 max-w-2xl mx-auto"
            >
              {mitgliedschaften.map((price) => (
                <motion.div
                  key={price.id}
                  variants={staggerItem}
                  className="price-card bg-yoga-sage/10 border border-yoga-sage/30 rounded-xl p-6 md:p-8 text-center"
                >
                  <h4 className="font-serif text-lg md:text-xl text-yoga-brown mb-2">{price.title}</h4>
                  <div className="font-serif text-3xl md:text-4xl text-yoga-brown mb-2 md:mb-3">{price.price}</div>
                  {price.description && (
                    <p className="font-sans text-sm md:text-base text-yoga-brown/70 mb-1 md:mb-2">{price.description}</p>
                  )}
                  {price.validity && (
                    <p className="font-sans text-xs md:text-sm text-yoga-sage">{price.validity}</p>
                  )}
                </motion.div>
              ))}
            </motion.div>
            <motion.p {...fadeUp} className="font-sans text-xs md:text-sm text-yoga-brown/50 text-center mt-4 md:mt-6">
              (Personal Yoga sind nicht Teil der Mitgliedschaften)
            </motion.p>
          </>
        )}
      </div>
    </section>
  );
};

// Events Section
const EventsSection = () => {
  const [events, setEvents] = useState([]);

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const response = await axios.get(`${API}/events`);
      setEvents(response.data);
    } catch (error) {
      console.error("Error fetching events:", error);
    }
  };

  if (events.length === 0) {
    return null;
  }

  return (
    <section id="events" className="py-16 md:py-32 bg-yoga-sand/30" data-testid="events-section">
      <div className="max-w-7xl mx-auto px-4 md:px-12">
        <motion.h2 {...fadeUp} className="font-serif text-3xl md:text-5xl text-yoga-brown text-center mb-8 md:mb-12">
          Aktuelle Veranstaltungen
        </motion.h2>

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className={`grid gap-6 md:gap-8 ${events.length === 1 ? 'grid-cols-1 max-w-md mx-auto' : events.length === 2 ? 'grid-cols-1 md:grid-cols-2 max-w-3xl mx-auto' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'}`}
        >
          {events.map((event) => (
            <motion.div
              key={event.id}
              variants={staggerItem}
              className="bg-yoga-cream rounded-xl overflow-hidden shadow-sm hover:shadow-md transition-shadow"
            >
              {event.image_url && (
                <div className="aspect-square overflow-hidden">
                  <img 
                    src={event.image_url} 
                    alt={event.title}
                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                  />
                </div>
              )}
              <div className="p-4 md:p-6">
                <div className="flex items-center gap-2 text-yoga-sage mb-2 md:mb-3">
                  <Clock size={14} strokeWidth={1.5} />
                  <span className="font-sans text-xs md:text-sm">{event.date} {event.time && `| ${event.time}`}</span>
                </div>
                <h3 className="font-serif text-lg md:text-xl text-yoga-brown mb-2 md:mb-3">{event.title}</h3>
                <p className="font-sans text-sm text-yoga-brown/70 mb-3 md:mb-4">{event.description}</p>
                {event.location && (
                  <div className="flex items-center gap-2 text-yoga-brown/50">
                    <MapPin size={12} strokeWidth={1.5} />
                    <span className="font-sans text-xs md:text-sm">{event.location}</span>
                  </div>
                )}
                {event.price && (
                  <div className="mt-2 md:mt-3 font-sans text-sm md:text-base text-yoga-brown font-medium">{event.price}</div>
                )}
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// About Section
const UeberMichSection = () => {
  return (
    <section id="ueber-mich" className="py-16 md:py-32 bg-yoga-cream" data-testid="ueber-mich-section">
      {/* Mobile: Full width image */}
      <div className="md:hidden mb-8">
        <img
          src="https://customer-assets.emergentagent.com/job_flow-wellness-3/artifacts/h1kgp90d_SUB_0665.jpg"
          alt="Daniela Kizay - Yogalehrerin"
          className="w-full h-auto"
        />
      </div>
      
      <div className="hidden md:grid grid-cols-2 gap-12 items-center">
        {/* Image - Desktop only, left-aligned to screen edge */}
        <div className="aspect-[3/4]">
          <img
            src="https://customer-assets.emergentagent.com/job_flow-wellness-3/artifacts/h1kgp90d_SUB_0665.jpg"
            alt="Daniela Kizay - Yogalehrerin"
            className="w-full h-full object-cover object-top"
          />
        </div>

        {/* Text */}
        <motion.div {...fadeUp} className="pr-12">
          <h2 className="font-serif text-3xl md:text-5xl text-yoga-brown mb-6 md:mb-8">Über Mich</h2>
          <div className="space-y-4 md:space-y-6 font-sans text-yoga-brown/80 leading-relaxed text-sm md:text-base">
            <p>
              Ich bin Daniela, Mutter von zwei fantastischen Zwillingsmädchen und leidenschaftliche Yogalehrerin aus Frechen. Bevor ich meine Berufung im Yoga fand, war ich als professionelle Bühnentänzerin tätig und arbeitete anschließend als Rechtsanwältin. Die Liebe zur Bewegung und zur Musik begleitet mich seit meiner Kindheit – heute darf ich sie in meinen Yogakursen mit anderen teilen.
            </p>
            <p>
              2016 absolvierte ich meine 200-Stunden Vinyasa-Yoga-Ausbildung in der Vishnus Couch in Köln. 2020 entdeckte ich den Yoga-Stil Inside Flow, der für mich die perfekte Verbindung von Yoga, Musik und fließender Bewegung darstellt. Der Begründer dieses Yogastils, Young Ho Kim persönlich, bildete mich zur qualifizierten Inside Flow Lehrerin aus.
            </p>
            <p>
              Es erfüllt mich mit großer Freude, meine Begeisterung für Yoga, Bewegung, Musik und Achtsamkeit an meine Schüler weiterzugeben und sie auf ihrem ganz persönlichen Yogaweg zu begleiten.
            </p>
          </div>
        </motion.div>
      </div>

      {/* Mobile: Text only */}
      <div className="md:hidden px-6">
        <h2 className="font-serif text-3xl text-yoga-brown mb-6">Über Mich</h2>
        <div className="space-y-4 font-sans text-yoga-brown/80 leading-relaxed text-sm">
          <p>
            Ich bin Daniela, Mutter von zwei fantastischen Zwillingsmädchen und leidenschaftliche Yogalehrerin aus Frechen. Bevor ich meine Berufung im Yoga fand, war ich als professionelle Bühnentänzerin tätig und arbeitete anschließend als Rechtsanwältin. Die Liebe zur Bewegung und zur Musik begleitet mich seit meiner Kindheit – heute darf ich sie in meinen Yogakursen mit anderen teilen.
          </p>
          <p>
            2016 absolvierte ich meine 200-Stunden Vinyasa-Yoga-Ausbildung in der Vishnus Couch in Köln. 2020 entdeckte ich den Yoga-Stil Inside Flow, der für mich die perfekte Verbindung von Yoga, Musik und fließender Bewegung darstellt. Der Begründer dieses Yogastils, Young Ho Kim persönlich, bildete mich zur qualifizierten Inside Flow Lehrerin aus.
          </p>
          <p>
            Es erfüllt mich mit großer Freude, meine Begeisterung für Yoga, Bewegung, Musik und Achtsamkeit an meine Schüler weiterzugeben und sie auf ihrem ganz persönlichen Yogaweg zu begleiten.
          </p>
        </div>
      </div>
    </section>
  );
};

// Contact Section
const KontaktSection = () => {
  return (
    <section id="kontakt" className="py-16 md:py-24 pb-8 md:pb-12 bg-yoga-sand/30" data-testid="kontakt-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.h2 {...fadeUp} className="font-serif text-3xl md:text-5xl text-yoga-brown text-center mb-8 md:mb-12">
          Kontakt
        </motion.h2>

        <motion.div {...fadeUp} className="max-w-xl mx-auto text-center">
          <p className="font-sans text-sm md:text-base text-yoga-brown/70 mb-6 md:mb-8 px-4">
            Du hast Fragen zu meinen Kursen oder möchtest dich anmelden? Ich freue mich auf deine Nachricht!
          </p>

          <div className="space-y-4 md:space-y-6">
            <a 
              href="mailto:daniela@kizay.de" 
              className="flex items-center justify-center gap-3 text-yoga-brown hover:text-yoga-sage transition-colors text-base md:text-lg"
              data-testid="contact-email"
            >
              <Mail size={20} strokeWidth={1.5} />
              <span className="font-sans">daniela@kizay.de</span>
            </a>
            <a 
              href="tel:+491778117539" 
              className="flex items-center justify-center gap-3 text-yoga-brown hover:text-yoga-sage transition-colors text-base md:text-lg"
              data-testid="contact-phone"
            >
              <Phone size={20} strokeWidth={1.5} />
              <span className="font-sans">+49 (0) 177 8117539</span>
            </a>
            <div className="flex items-center justify-center gap-3 text-yoga-brown/70">
              <MapPin size={20} strokeWidth={1.5} />
              <span className="font-sans">Frechen</span>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  const navigate = useNavigate();
  
  return (
    <footer className="bg-yoga-sand/30 text-yoga-brown pt-2 pb-6 md:pt-4 md:pb-8" data-testid="footer">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="border-t border-yoga-brown/20 pt-6 md:pt-8">
          <div className="flex flex-col items-center gap-6 md:flex-row md:justify-between">
            <div className="text-center md:text-left">
              <h3 className="font-serif text-xl md:text-2xl mb-1 md:mb-2">DanyKizayYoga</h3>
              <p className="font-sans text-yoga-brown/70 text-xs md:text-sm">Vinyasa Yoga & Inside Flow in Frechen</p>
          </div>

          <div className="flex items-center gap-4 md:gap-6">
            <Link to="/impressum" className="font-sans text-xs md:text-sm text-yoga-brown/70 hover:text-yoga-brown transition-colors" data-testid="footer-impressum">
              Impressum
            </Link>
            <Link to="/datenschutz" className="font-sans text-xs md:text-sm text-yoga-brown/70 hover:text-yoga-brown transition-colors" data-testid="footer-datenschutz">
              Datenschutz
            </Link>
          </div>

          <div 
            className="font-sans text-xs md:text-sm text-yoga-brown/50 cursor-pointer hover:text-yoga-brown/70 transition-colors"
            onClick={() => navigate('/admin')}
            data-testid="footer-admin-link"
          >
            © {new Date().getFullYear()} DanyKizayYoga
          </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Main Page Component
const HomePage = () => {
  return (
    <div className="min-h-screen bg-yoga-cream">
      <Navigation />
      <HeroSection />
      <YogaArtenSection />
      <GalleryTopSection />
      <WochenplanSection />
      <GalleryBottomSection />
      <PreiseSection />
      <EventsSection />
      <UeberMichSection />
      <KontaktSection />
      <Footer />
    </div>
  );
};

export default HomePage;
